import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChatStore } from '../store/chatStore';
import { useChatSearchStore } from '../store/chatSearchStore';
import { loadOlderPage } from '../utils/chatOlderPage';
import { cancelChatSearch } from './useChatSearch';
import { useNotificationStore } from '../store/notificationStore';
import i18n from '../i18n';

/**
 * Jump to a message by `server_seq` (PLAN §7). Runs inside ChatMessageView
 * because it needs the view's refs.
 *
 * Pages older history through the SAME path as scroll-to-top
 * (utils/chatOlderPage) until the target is in the buffer — no disjoint
 * window, so the buffer stays contiguous (D12). While it runs it holds
 * `loadingOlderRef`, so scroll paging cannot fetch the same page twice, and
 * releases it after. After MAX_JUMP_PAGES pages it stops with status 'far';
 * the result card's "Keep loading" re-requests the jump with a fresh budget.
 *
 * A newer jump or a channel switch cancels a running one (token), silently.
 */

export const MAX_JUMP_PAGES = 40;
export const HIGHLIGHT_MS = 2400;
const WAIT_FRAMES = 300; // ≈5 s for an in-flight scroll fetch to settle
const FIND_FRAMES = 20;

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
const isActive = (channelId) => useChatStore.getState().activeChannelId === channelId;

export default function useJumpToMessage({
  channelId,
  getViewport,
  loadingOlderRef,
  exhaustedRef,
  atBottomRef,
  restoreFromBottomRef,
}) {
  const tokenRef = useRef(0);
  const highlightTimerRef = useRef(null);
  const jumpRequest = useChatSearchStore((s) => s.jumpRequest);

  // A conversation switch cancels a running jump AND an in-flight search (its
  // late response must never land in the new conversation's panel), and drops
  // the previous conversation's input and results (Discord clears it too).
  useEffect(() => {
    const s = useChatSearchStore.getState();
    if (s.channelId !== channelId) {
      cancelChatSearch();
      s.reset(channelId);
    }
    return () => {
      tokenRef.current += 1;
    };
  }, [channelId]);

  useEffect(
    () => () => {
      clearTimeout(highlightTimerRef.current);
    },
    []
  );

  const reveal = useCallback(
    async (seq, token) => {
      const store = useChatSearchStore.getState();
      atBottomRef.current = false;
      let el = null;
      for (let i = 0; i <= FIND_FRAMES && !el; i += 1) {
        el = getViewport()?.querySelector(`[data-seq="${seq}"]`) || null;
        // eslint-disable-next-line no-await-in-loop
        if (!el) await nextFrame();
        if (tokenRef.current !== token) return;
      }
      el?.scrollIntoView?.({ block: 'center' });
      store.setHighlight({ channelId, seq });
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        const h = useChatSearchStore.getState().highlight;
        if (h && h.channelId === channelId && h.seq === seq) useChatSearchStore.getState().clearHighlight();
      }, HIGHLIGHT_MS);
      store.setJumpStatus('idle', seq);
      store.setJumpedAway(true);
    },
    [channelId, getViewport, atBottomRef]
  );

  const run = useCallback(
    async ({ seq }) => {
      const token = ++tokenRef.current;
      const store = useChatSearchStore.getState();
      for (let i = 0; i < WAIT_FRAMES && loadingOlderRef.current; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await nextFrame();
        if (tokenRef.current !== token) return;
      }
      if (tokenRef.current !== token || !isActive(channelId)) return;
      loadingOlderRef.current = true;
      store.setJumpStatus('loading', seq);
      let outcome = 'abort';
      try {
        let pages = 0;
        for (;;) {
          const buf = useChatStore.getState().messagesByChannel[channelId] || [];
          if (buf.some((m) => m.server_seq === seq)) {
            outcome = 'found';
            break;
          }
          const oldest = buf.find((m) => m.server_seq != null);
          if ((oldest && oldest.server_seq < seq) || exhaustedRef.current.has(channelId) || !oldest) {
            outcome = 'missing';
            break;
          }
          if (pages >= MAX_JUMP_PAGES) {
            outcome = 'far';
            break;
          }
          const vp = getViewport();
          // eslint-disable-next-line no-await-in-loop
          const r = await loadOlderPage(channelId, oldest.server_seq, undefined, {
            beforePrepend: () => {
              if (vp && isActive(channelId)) restoreFromBottomRef.current = vp.scrollHeight - vp.scrollTop;
            },
          });
          if (tokenRef.current !== token || !isActive(channelId)) break;
          if (r.exhausted) exhaustedRef.current.add(channelId);
          pages += 1;
        }
      } catch {
        outcome = 'missing';
      } finally {
        // Same rule as onScroll: only release the guard on the channel we
        // fetched for — after a switch the channel-change effect owns it.
        if (isActive(channelId)) loadingOlderRef.current = false;
      }
      if (tokenRef.current !== token) return;
      if (outcome === 'found') await reveal(seq, token);
      else if (outcome === 'far') store.setJumpStatus('far', seq);
      else if (outcome === 'missing') {
        store.setJumpStatus('missing', seq);
        useNotificationStore.getState().info(i18n.t('chat.search.jumpMissing'));
      } else store.setJumpStatus('idle', seq);
    },
    [channelId, getViewport, loadingOlderRef, exhaustedRef, restoreFromBottomRef, reveal]
  );

  useEffect(() => {
    if (!jumpRequest || jumpRequest.channelId !== channelId) return;
    // Handled already (this mount or an earlier one): a remount must not replay it.
    if (useChatSearchStore.getState().handledJumpNonce === jumpRequest.nonce) return;
    useChatSearchStore.getState().markJumpHandled(jumpRequest.nonce);
    run(jumpRequest);
  }, [jumpRequest, channelId, run]);

  /** Called from onScroll: reaching the bottom again ends the jump (pill, highlight, request). */
  const onScrolled = useCallback((atBottom) => {
    if (atBottom && useChatSearchStore.getState().jumpedAway) {
      useChatSearchStore.getState().dropJump();
    }
  }, []);

  const jumpToPresent = useCallback(() => {
    tokenRef.current += 1; // cancel a jump still paging
    clearTimeout(highlightTimerRef.current);
    const vp = getViewport();
    if (vp) vp.scrollTop = vp.scrollHeight;
    atBottomRef.current = true;
    useChatSearchStore.getState().dropJump();
  }, [getViewport, atBottomRef]);

  return useMemo(() => ({ onScrolled, jumpToPresent }), [onScrolled, jumpToPresent]);
}
